// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskExternalId, HexTriplet } from '@evolvemep/api-ts';

import type { TypeSafePropertyResult } from 'components/Autodesk/extensions/propertyHelpers';
import type { ForgeViewerSelection } from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import type { TypeSafeBulkPropertiesOptions } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { getBasicStoreListeners } from 'helpers/getBasicStoreListeners';

/**
 * Viewer commands registered by `APSViewerStoreExtension` on load and cleared on unload.
 * Most methods directly map to a primitive Autodesk Viewer API call, with a few noted exceptions.
 */
export type APSViewerCommands = {
  /**
   * Isolate makes a set of DBIds the only visible objects on the model.
   * In addition, you can optionally fit the isolated objects to the view.
   **/
  isolate: (dbIds: AutodeskDbId[], shouldFitToView?: boolean) => void;
  /** Fits the set of DBIds to the camera. When an empty list is provided, the viewer attempts to fit all visible objects. */
  fitToView: (dbIds: AutodeskDbId[]) => void;
  /** Shows all objects on the model. */
  showAll: () => void;
  /** When true, hidden objects still show as transparent objects, but are still unselectable. */
  setGhosting: (enabled: boolean) => void;
  /** Clears the current selection in the model. */
  clearSelection: () => void;
  /** Hides a set of DBIds from the model. */
  hide: (dbIds: AutodeskDbId[]) => void;
  /** Shows a set of DBIds from the model. This is in contrast to "isolate", which also hides any DBIds not in the list.*/
  show: (dbIds: AutodeskDbId[]) => void;
  /** Sets a given DBId as locked or unlocked. Locking a DBId makes it unselectable. */
  lockSelection: (dbIds: AutodeskDbId[], locked: boolean) => void;
  /**
   * Colors a set of DBIDs with a given hex color.
   *
   * setThemingColor is a slight exception to the 1-to-1 mapping.
   * It accepts multiple dbIds and converts your hex color to a THREE.Vector4 before applying it.
   **/
  setThemingColor: (dbIds: AutodeskDbId[], color: HexTriplet, recursive?: boolean) => void;
  /** Clears all theming colors applied to the model. */
  clearThemingColors: () => void;
  /**
   * Sets the active navigation tool in the viewer (or, unusually, selection tool).
   * Uses the viewer's default if no toolName is provided.
   **/
  setActiveNavigationTool: (toolName?: string) => void;
  /** Retrieves bulk properties (things like length, radius, etc. stored on the object) for a set of DBIds. */
  getBulkProperties: <T extends string>(
    dbIds: AutodeskDbId[],
    options?: TypeSafeBulkPropertiesOptions<T>,
  ) => Promise<TypeSafePropertyResult<T>[]>;
};

type APSViewerSnapshot = {
  /** A list of DBids currently isolated by the model. Updated by the extension whenever isolation changes. */
  isolatedDbIds: AutodeskDbId[];
  /** A list of currently selected objects in the model. Updated by the extension whenever selection changes. */
  selection: ForgeViewerSelection[];
  /**
   * A mapping of externalId to dbId for all objects in the model. External IDs are stable, DBIds are not.
   * We store external IDs in our database. For example, ${@link MachineElement['sourceIds']}, and this allows
   * us to find those external IDs in the model and, as an example, isolate them.
   *
   * Updated by the extension once the model root has loaded. Initially null.
   **/
  externalIdLookup: Record<AutodeskExternalId, AutodeskDbId> | null;
  /** `true` once the model root has finished loading; `false` before load and after unload. */
  isModelReady: boolean;
  /** The set of viewer commands registered by the extension. Null before load and after unload. */
  commands: APSViewerCommands | null;
};

/**
 * A external store for viewer-wide state: the current isolation set and selection.
 * Intended for use with React's `useSyncExternalStore`.
 *
 * Kept up-to-date by `APSViewerStoreExtension`, which intercepts Autodesk Viewer events.
 * All state mutations go through explicit setters so that every change notifies subscribers
 * and triggers automatic React re-renders.
 */
export const createAPSViewerStore = () => {
  let snapshot: APSViewerSnapshot = {
    isolatedDbIds: [],
    selection: [],
    externalIdLookup: null,
    isModelReady: false,
    commands: null,
  };

  const { subscribe, notifyListeners } = getBasicStoreListeners();

  const update = (partial: Partial<APSViewerSnapshot>) => {
    snapshot = { ...snapshot, ...partial };
    notifyListeners();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe,
    setIsolatedDbIds: (isolatedDbIds: AutodeskDbId[]) => update({ isolatedDbIds }),
    setSelection: (selection: ForgeViewerSelection[]) => update({ selection }),
    setExternalIdLookup: (externalIdLookup: Record<AutodeskExternalId, AutodeskDbId> | null) =>
      update({ externalIdLookup, isModelReady: externalIdLookup !== null }),
    /**
     *  Resets the model-derived state but does not unregister commands.
     *  Called when the extension or the model is unloaded.
     **/
    clear: () => update({ isolatedDbIds: [], selection: [], externalIdLookup: null, isModelReady: false }),

    registerCommands: (commands: APSViewerCommands) => {
      update({ commands });
    },
    unregisterCommands: () => {
      update({ commands: null });
    },
  };
};

export type APSViewerStore = ReturnType<typeof createAPSViewerStore>;
